import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { getBalance, getEstadoResultados } from '../services/api';

// Lleva el toolbar a la fila de las pestañas (Balance / Estado de Resultados).
function Portal({ id, children }) {
  const [el, setEl] = useState(null);
  useEffect(() => { setEl(document.getElementById(id)); return () => setEl(null); }, [id]);
  return el ? createPortal(children, el) : null;
}

const fmtCOP = n => Number(n || 0).toLocaleString('es-CO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const hoy = () => new Date().toISOString().slice(0, 10);
const ene1 = () => `${new Date().getFullYear()}-01-01`;

export default function EstadosFinancieros() {
  const [tab, setTab] = useState('balance');
  return (
    <div style={S.container}>
      <div style={S.header}>
        <p style={S.sub}>Balance General y Estado de Resultados — derivados de los comprobantes contabilizados</p>
      </div>
      <div style={{ ...S.tabs, justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={tab === 'balance' ? S.tabActive : S.tab} onClick={() => setTab('balance')}>Balance General</button>
          <button style={tab === 'er'      ? S.tabActive : S.tab} onClick={() => setTab('er')}>Estado de Resultados</button>
        </div>
        <div id="ef-toolbar" style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }} />
      </div>
      {tab === 'balance' ? <Balance /> : <EstadoResultados />}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
function Balance() {
  const [fecha, setFecha] = useState(hoy());
  const [data, setData]   = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const cargar = async () => {
    setLoading(true); setError('');
    try {
      const { data } = await getBalance({ fecha });
      setData(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al cargar balance');
    } finally { setLoading(false); }
  };
  useEffect(() => { cargar(); /* eslint-disable-line */ }, []);

  const seccion = (cod, titulo) => {
    if (!data) return null;
    const cuentas = data.cuentas.filter(c => c.codigo.startsWith(cod));
    if (cuentas.length === 0) return null;
    return (
      <div style={S.seccion}>
        <h3 style={S.seccionTitle}>{titulo}</h3>
        <table style={S.table}>
          <tbody>
            {cuentas.map(c => (
              <tr key={c.codigo} style={c.acepta_movimiento ? {} : { fontWeight: 600, background: '#f8fafc' }}>
                <td style={{ ...S.td, paddingLeft: 8 + (c.nivel - 1) * 18, fontFamily: c.acepta_movimiento ? 'inherit' : 'inherit' }}>
                  <span style={{ fontFamily: 'monospace', color: '#64748b', marginRight: 8 }}>{c.codigo}</span>
                  {c.nombre}
                </td>
                <td style={{ ...S.td, textAlign: 'right', fontFamily: 'monospace' }}>{fmtCOP(c.saldo)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div>
      <Portal id="ef-toolbar">
        <label style={S.lbl}>Saldos al</label>
        <input type="date" value={fecha} onChange={e => setFecha(e.target.value)} style={S.input} />
        <button onClick={cargar} style={S.btnPri}>Generar</button>
      </Portal>
      {error && <div style={S.error}>{error}</div>}
      {loading && <div style={S.loading}>Cargando…</div>}
      {data && !loading && (
        <>
          {seccion('1', 'ACTIVO')}
          {seccion('2', 'PASIVO')}
          {seccion('3', 'PATRIMONIO')}
          <div style={S.summary}>
            <Row label="Total Activo"     value={data.total_activo}   bold />
            <Row label="Total Pasivo"     value={data.total_pasivo}   bold />
            <Row label="Total Patrimonio (incluye resultado del ejercicio)" value={data.total_patrimonio} bold />
            <Row label="Utilidad/Pérdida del ejercicio" value={data.utilidad_ejercicio} sub />
            {Math.abs(data.cuadre) > 0.01 ? (
              <Row label="⚠ Descuadre" value={data.cuadre} warn />
            ) : (
              <div style={{ ...S.row, color: '#15803d', fontWeight: 600 }}>
                <span>✓ Activo = Pasivo + Patrimonio</span><span></span>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
function EstadoResultados() {
  const [filtro, setFiltro] = useState({ fecha_desde: ene1(), fecha_hasta: hoy() });
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState('');

  const cargar = async () => {
    setLoading(true); setError('');
    try {
      const { data } = await getEstadoResultados(filtro);
      setData(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al cargar Estado de Resultados');
    } finally { setLoading(false); }
  };
  useEffect(() => { cargar(); /* eslint-disable-line */ }, []);

  const seccion = (cod, titulo) => {
    if (!data) return null;
    const cuentas = data.cuentas.filter(c => c.codigo.startsWith(cod));
    if (cuentas.length === 0) return null;
    return (
      <div style={S.seccion}>
        <h3 style={S.seccionTitle}>{titulo}</h3>
        <table style={S.table}>
          <tbody>
            {cuentas.map(c => (
              <tr key={c.codigo} style={c.acepta_movimiento ? {} : { fontWeight: 600, background: '#f8fafc' }}>
                <td style={{ ...S.td, paddingLeft: 8 + (c.nivel - 1) * 18 }}>
                  <span style={{ fontFamily: 'monospace', color: '#64748b', marginRight: 8 }}>{c.codigo}</span>
                  {c.nombre}
                </td>
                <td style={{ ...S.td, textAlign: 'right', fontFamily: 'monospace' }}>{fmtCOP(c.saldo)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div>
      <Portal id="ef-toolbar">
        <label style={S.lbl}>Desde</label>
        <input type="date" value={filtro.fecha_desde} onChange={e => setFiltro(f => ({ ...f, fecha_desde: e.target.value }))} style={S.input} />
        <label style={S.lbl}>Hasta</label>
        <input type="date" value={filtro.fecha_hasta} onChange={e => setFiltro(f => ({ ...f, fecha_hasta: e.target.value }))} style={S.input} />
        <button onClick={cargar} style={S.btnPri}>Generar</button>
      </Portal>
      {error && <div style={S.error}>{error}</div>}
      {loading && <div style={S.loading}>Cargando…</div>}
      {data && !loading && (
        <>
          {seccion('4', 'INGRESOS')}
          {seccion('5', 'GASTOS')}
          {seccion('6', 'COSTO DE VENTAS')}
          {seccion('7', 'COSTOS DE PRODUCCIÓN')}
          <div style={S.summary}>
            <Row label="Ingresos"         value={data.ingresos}          bold />
            <Row label="(–) Gastos"       value={data.gastos}            sub />
            <Row label="(–) Costo de ventas"     value={data.costo_ventas}      sub />
            <Row label="(–) Costos de producción" value={data.costo_produccion} sub />
            <Row label={data.utilidad >= 0 ? 'UTILIDAD del ejercicio' : 'PÉRDIDA del ejercicio'}
                 value={data.utilidad} bold big positive={data.utilidad >= 0} />
          </div>
        </>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
function Row({ label, value, bold, sub, big, warn, positive }) {
  return (
    <div style={{
      ...S.row,
      fontWeight: bold ? 700 : 400,
      color: warn ? '#b91c1c' : (big ? (positive ? '#15803d' : '#b91c1c') : '#1e293b'),
      fontSize: big ? 18 : 14,
      borderTop: bold ? '1px solid #e2e8f0' : 'none',
      paddingTop: bold ? 8 : 4, paddingBottom: 4,
      paddingLeft: sub ? 16 : 0,
    }}>
      <span>{label}</span>
      <span style={{ fontFamily: 'monospace' }}>{fmtCOP(value)}</span>
    </div>
  );
}

const S = {
  container: { padding: 24, maxWidth: 1100, margin: '0 auto' },
  header:    { marginBottom: 16 },
  title:     { fontSize: 24, fontWeight: 700, color: '#0f172a', margin: 0 },
  sub:       { color: '#64748b', margin: '4px 0 0', fontSize: 13 },
  tabs:      { display: 'flex', gap: 8, borderBottom: '1px solid #334155', marginBottom: 16, paddingBottom: 4 },
  tab:       { padding: '8px 16px', background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 14, color: '#94a3b8', borderBottom: '2px solid transparent', fontWeight: 500 },
  tabActive: { padding: '8px 16px', background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 14, color: '#f1f5f9', borderBottom: '2px solid #3b82f6', fontWeight: 600 },
  toolbar:   { display: 'flex', gap: 10, alignItems: 'center', marginBottom: 18, flexWrap: 'wrap' },
  lbl:       { fontSize: 12, color: '#64748b', fontWeight: 500 },
  input:     { padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, background: '#fff' },
  btnPri:    { padding: '8px 16px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600 },
  error:     { background: '#fee2e2', border: '1px solid #fca5a5', color: '#991b1b', padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 13 },
  loading:   { padding: 40, textAlign: 'center', color: '#64748b' },
  seccion:      { background: '#fff', borderRadius: 8, marginBottom: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden' },
  seccionTitle: { margin: 0, padding: '10px 14px', background: '#0f172a', color: '#fff', fontSize: 13, letterSpacing: 0.5 },
  table:     { width: '100%', borderCollapse: 'collapse' },
  td:        { padding: '4px 14px', fontSize: 13, color: '#1e293b', borderBottom: '1px solid #f1f5f9' },
  summary:   { background: '#fff', borderRadius: 8, padding: 18, boxShadow: '0 1px 3px rgba(0,0,0,0.05)', marginTop: 12 },
  row:       { display: 'flex', justifyContent: 'space-between', padding: '4px 0' },
};
